/**
 * Hook to poll classification status for ALL of the user's deal rooms.
 *
 * The single-project sibling (`useClassificationStatus`) answers for one room,
 * which made the sidebar spinner follow the SELECTION: a second room finishing
 * its upload never showed one, and switching rooms moved the spinner (DOP-250).
 * Rooms are polled together, and the result is keyed by project id so a row can
 * ask about itself.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "../services/api";
import { getProjectsClassificationStatus } from "../services/classificationService";
import { USER_FILES_UPDATED_EVENT } from "../constants/events";

export interface ProjectClassificationState {
	isProcessing: boolean;
	processingCount: number;
}

export type ClassificationStatusMap = Map<string, ProjectClassificationState>;

const POLL_INTERVAL_MS = 3000;
const REFETCH_AFTER_UPLOAD_MS = 1000;

/** Stable empty map so a render before the first response is not a new object.
 *  Also what a disabled hook reports: "not known", not "nothing in progress". */
export const EMPTY_STATUSES: ClassificationStatusMap = new Map();

/**
 * True when some deal has fewer files still being read than before — files
 * finished classification, so their rows should turn ready. A count that only
 * appears or grows is an upload, which refreshes the tree on its own event.
 * A deal missing from `next` counts as finished.
 */
export function classificationFinishedSince(
	previous: ClassificationStatusMap,
	next: ClassificationStatusMap,
): boolean {
	for (const [projectId, state] of previous) {
		if (state.processingCount > (next.get(projectId)?.processingCount ?? 0)) {
			return true;
		}
	}
	return false;
}

export const useClassificationStatuses = (
	enabled: boolean = true,
): ClassificationStatusMap => {
	const [statuses, setStatuses] =
		useState<ClassificationStatusMap>(EMPTY_STATUSES);

	const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const enabledRef = useRef(enabled);
	// Which poll chain is the live one, and the ONLY cancellation mechanism —
	// lifecycle included. Every chain start bumps it; a response handler or a
	// schedule belonging to an older generation self-discards.
	//
	// Two things this replaces. An upload event that lands while a request is
	// in flight used to leave that request free to schedule its own next tick
	// — two permanent 3s chains — and its stale response could overwrite newer
	// state. And a separate "is mounted" ref used to gate polling from its own
	// effect: under React.StrictMode (which this app mounts under) effect
	// replay runs every cleanup before every setup, so the mounted ref was
	// still false when the polling effect re-ran and the restart silently
	// no-opped, then the mount effect re-armed the ref without starting a
	// poll. Rooms already classifying never spun until an upload event.
	// Cancellation therefore lives in the same effect that starts polling.
	const generationRef = useRef(0);

	const clearPollTimeout = useCallback(() => {
		if (timeoutRef.current !== null) {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
		}
	}, []);

	/** Retire every chain in flight: nothing scheduled, nothing can resume. */
	const invalidate = useCallback(() => {
		generationRef.current += 1;
		clearPollTimeout();
	}, [clearPollTimeout]);

	// One timeout ref for every chain, so it is always cleared before it is
	// set: a stale generation can never leave a timer behind.
	const schedule = useCallback(
		(generation: number, delayMs: number, run: () => void) => {
			if (generation !== generationRef.current) return;
			if (timeoutRef.current !== null) clearTimeout(timeoutRef.current);
			timeoutRef.current = setTimeout(run, delayMs);
		},
		[],
	);

	const poll = useCallback(
		async (generation: number) => {
			if (generation !== generationRef.current) return;

			try {
				const projects = await getProjectsClassificationStatus();

				// A newer chain started while this request was in flight — or
				// the hook was unmounted/disabled, which retires the chain the
				// same way. Either way this answer is no longer the current
				// one, so drop it instead of overwriting newer state.
				if (generation !== generationRef.current) return;

				const next: ClassificationStatusMap = new Map();
				let anyProcessing = false;
				for (const project of projects) {
					if (project.is_processing) anyProcessing = true;
					next.set(project.project_id, {
						isProcessing: project.is_processing,
						processingCount: project.pending + project.processing,
					});
				}
				setStatuses(next);

				// Poll only while there is something left to finish. A room
				// that completes clears its own entry on the next tick; the
				// last one to finish stops the polling.
				if (anyProcessing) {
					schedule(generation, POLL_INTERVAL_MS, () =>
						poll(generation),
					);
				}
			} catch (error) {
				console.warn("Classification statuses fetch failed:", error);
				// Stop polling on terminal errors (4xx); only retry on transient failures
				const isTerminal =
					error instanceof ApiError &&
					error.status >= 400 &&
					error.status < 500;
				if (!isTerminal) {
					schedule(generation, POLL_INTERVAL_MS, () =>
						poll(generation),
					);
				}
			}
		},
		[schedule],
	);

	/** Retire every chain in flight and start exactly one new one. */
	const restartPolling = useCallback(
		(delayMs: number = 0) => {
			if (!enabledRef.current) {
				invalidate();
				return;
			}
			const generation = ++generationRef.current;
			clearPollTimeout();
			if (delayMs > 0) {
				schedule(generation, delayMs, () => poll(generation));
			} else {
				void poll(generation);
			}
		},
		[clearPollTimeout, invalidate, poll, schedule],
	);

	useEffect(() => {
		enabledRef.current = enabled;

		if (enabled) {
			restartPolling();
		} else {
			invalidate();
			setStatuses(EMPTY_STATUSES);
		}

		// Unmount and effect replay both retire the live chain here. Because a
		// replay's setup runs right after and starts a fresh generation, an
		// already-classifying room keeps spinning across the replay.
		return invalidate;
	}, [enabled, restartPolling, invalidate]);

	// New uploads mean new pending work, so restart polling.
	useEffect(() => {
		const handleFilesUpdated = () => {
			// Small delay to let backend register the new files
			restartPolling(REFETCH_AFTER_UPLOAD_MS);
		};

		window.addEventListener(USER_FILES_UPDATED_EVENT, handleFilesUpdated);

		return () => {
			window.removeEventListener(
				USER_FILES_UPDATED_EVENT,
				handleFilesUpdated,
			);
		};
	}, [restartPolling]);

	return statuses;
};
